import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Edit } from "lucide-react";
import { toast } from "sonner";

function ImageField({ value, onChange }) {
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" }}); onChange(`${process.env.REACT_APP_BACKEND_URL}${r.data.url}`); toast.success("Uploaded"); }
    catch { toast.error("Upload failed"); }
  };
  return (
    <div className="flex items-center gap-2">
      {value && <img src={value} alt="" className="h-10 w-10 object-contain border rounded bg-white"/>}
      <Input value={value||""} placeholder="Image URL or upload" onChange={e=>onChange(e.target.value)}/>
      <label className="text-xs px-3 py-2 border rounded cursor-pointer whitespace-nowrap hover:bg-[#F8FAFC]">Upload<input type="file" hidden accept="image/*" onChange={e=>e.target.files[0] && upload(e.target.files[0])}/></label>
    </div>
  );
}

function FieldInput({ f, value, onChange }) {
  if (f.type === "textarea") return <Textarea rows={3} value={value||""} onChange={e=>onChange(e.target.value)}/>;
  if (f.type === "switch") return <div className="mt-1"><Switch checked={!!value} onCheckedChange={onChange}/></div>;
  if (f.type === "image") return <ImageField value={value} onChange={onChange}/>;
  if (f.type === "number") return <Input type="number" value={value||0} onChange={e=>onChange(parseInt(e.target.value)||0)}/>;
  return <Input value={value||""} onChange={e=>onChange(e.target.value)}/>;
}

function CellValue({ k, v }) {
  if (k === "logo" || k === "image") return v ? <img src={v} alt="" className="h-8 w-12 object-contain"/> : "-";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v ?? "-");
}

function ResourceAdmin({ title, endpoint, listEndpoint, fields, columns, empty }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { api.get(listEndpoint || endpoint).then(r => setItems(r.data)); }, [listEndpoint, endpoint]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    try {
      if (editing.id) await api.put(`${endpoint}/${editing.id}`, editing);
      else await api.post(endpoint, editing);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`${endpoint}/${id}`); load(); };
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading font-extrabold text-2xl">{title}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="btn-orange" onClick={()=>setEditing({...empty})}><Plus className="w-4 h-4 mr-1"/> Add</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>{editing?.id ? "Edit" : "Add"}</DialogTitle></DialogHeader>
            {editing && <div className="space-y-3">
              {fields.map(f => (
                <div key={f.name}>
                  <Label>{f.label}</Label>
                  <FieldInput f={f} value={editing[f.name]} onChange={v=>setEditing({...editing, [f.name]: v})}/>
                </div>
              ))}
              <Button className="btn-orange w-full" onClick={save}>Save</Button>
            </div>}
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border border-[#E2E8F0] rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] text-left"><tr>{columns.map(c=><th key={c} className="p-3">{c}</th>)}<th></th></tr></thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-t border-[#E2E8F0]">
                {columns.map(c => { const k = c.toLowerCase().replace(/ /g, "_");
                  return <td key={c} className="p-3"><CellValue k={k} v={it[k]}/></td>; })}
                <td className="pr-3 text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" onClick={()=>{setEditing(it); setOpen(true);}}><Edit className="w-4 h-4"/></Button>
                  <Button size="icon" variant="ghost" onClick={()=>del(it.id)}><Trash2 className="w-4 h-4 text-red-500"/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const AdminCategories = () => <ResourceAdmin title="Categories" endpoint="/categories"
  empty={{ name: "", description: "", image: "", sort_order: 0, published: true }}
  fields={[{name:"name", label:"Name"},{name:"description", label:"Description", type:"textarea"},{name:"image", label:"Image", type:"image"},{name:"sort_order", label:"Sort Order", type:"number"},{name:"published", label:"Published", type:"switch"}]}
  columns={["Name", "Slug", "Sort Order", "Published"]}/>;

export const AdminBrands = () => <ResourceAdmin title="Brands" endpoint="/brands" listEndpoint="/brands/admin/all"
  empty={{ name: "", logo: "", published: true, archived: false }}
  fields={[{name:"name", label:"Name"},{name:"logo", label:"Brand Logo", type:"image"},{name:"published", label:"Enabled (visible on site)", type:"switch"},{name:"archived", label:"Archived", type:"switch"}]}
  columns={["Name", "Slug", "Logo", "Published", "Archived"]}/>;

export const AdminReviews = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/reviews/admin/all").then(r => setItems(r.data)); }, [open]);
  const save = async () => {
    if (editing.id) await api.put(`/reviews/${editing.id}`, editing);
    else await api.post("/reviews", editing);
    setOpen(false); toast.success("Saved");
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading font-extrabold text-2xl">Reviews</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="btn-orange" onClick={()=>setEditing({reviewer_name:"", rating:5, review_text:"", source:"Google", published:true})}><Plus className="w-4 h-4 mr-1"/> Add Review</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>Review</DialogTitle></DialogHeader>
            {editing && <div className="space-y-3">
              <div><Label>Reviewer</Label><Input value={editing.reviewer_name} onChange={e=>setEditing({...editing, reviewer_name: e.target.value})}/></div>
              <div><Label>Rating</Label><Input type="number" min={1} max={5} value={editing.rating} onChange={e=>setEditing({...editing, rating: parseInt(e.target.value)||5})}/></div>
              <div><Label>Review Text</Label><Textarea rows={4} value={editing.review_text} onChange={e=>setEditing({...editing, review_text: e.target.value})}/></div>
              <div><Label>Source</Label><Input value={editing.source} onChange={e=>setEditing({...editing, source: e.target.value})}/></div>
              <label className="flex items-center gap-2"><Switch checked={editing.published} onCheckedChange={v=>setEditing({...editing, published: v})}/> Published</label>
              <Button className="btn-orange w-full" onClick={save}>Save</Button>
            </div>}
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map(r => (
          <div key={r.id} className="bg-white border border-[#E2E8F0] rounded-md p-4">
            <div className="flex justify-between"><div className="font-bold">{r.reviewer_name}</div><div className="text-amber-500">{"★".repeat(r.rating)}</div></div>
            <div className="text-sm text-slate-700 mt-1">{r.review_text}</div>
            <div className="text-xs text-slate-500 mt-2 flex justify-between">{r.source} · {r.published ? "Published" : "Hidden"}
              <button className="text-red-500" onClick={async()=>{ if(window.confirm("Delete?")) { await api.delete(`/reviews/${r.id}`); setOpen(o=>o); setItems(items.filter(x=>x.id!==r.id)); }}}>Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AdminOffers = () => <ResourceAdmin title="Offers" endpoint="/offers"
  empty={{ title: "", description: "", discount: "", active: true }}
  fields={[{name:"title", label:"Title"},{name:"description", label:"Description", type:"textarea"},{name:"discount", label:"Discount Label"},{name:"active", label:"Active", type:"switch"}]}
  columns={["Title", "Discount", "Active"]}/>;
